import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Dictionary, filterSuggestionItems } from '@blocknote/core';
import type { DragHandleMenuProps, SideMenuProps } from '@blocknote/react';
import {
  BasicTextStyleButton,
  BlockTypeSelect,
  CreateLinkButton,
  DragHandleMenu,
  FormattingToolbar,
  FormattingToolbarController,
  RemoveBlockItem,
  SideMenu,
  SideMenuController,
  SuggestionMenuController,
  useCreateBlockNote,
  useDictionary,
  useEditorChange,
} from '@blocknote/react';
import { BlockNoteView, ShadCNDefaultComponents } from '@blocknote/shadcn';
import type { Middleware } from '@floating-ui/react';
import { flip, shift, size } from '@floating-ui/react';

import {
  DropdownMenuContent as AppDropdownMenuContent,
  DropdownMenuItem as AppDropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip as AppTooltip,
  TooltipContent as AppTooltipContent,
  TooltipProvider as AppTooltipProvider,
  TooltipTrigger as AppTooltipTrigger,
} from '@/components/ui/tooltip';
import type { ContextVariableOption } from '@/shared/types/autocomplete';
import type { Dialog } from '@/shared/types/dialog';

import { getGuidelinesEditorDefaults } from './config';
import {
  buildContextVariableLookup,
  createMediaStrippingPasteHandler,
  denormalizeContextVariableNodes,
  denormalizeLocalToolNodes,
  filterBlockTypeSelectItems,
  getContextVariableMenuItems,
  getCustomSlashMenuItems,
  getLocalToolMenuItems,
  getWorkflowMenuItems,
  normalizeBlocksWithContextVariables,
  normalizeBlocksWithLocalTools,
  parseMarkdownToBlocks,
  resolveLocalToolLabels,
  schema,
} from './editorUtils';

import '@blocknote/shadcn/style.css';
import './BlockNoteEditor.scss';

type Props = {
  initialMarkdown: string;
  onSave?: (markdownToSave: string) => void;
  dictionary?: Dictionary;
  onEditorReady?: (editor: {
    appendContent: (markdown: string) => void;
    getMarkdown: () => Promise<string>;
  }) => void;
  editable?: boolean;
  onChange?: () => void;
  enabledTools?: string[];
  dialogs?: Dialog[];
  onNavigateToLocalTools?: () => void;
  /** Context variable options for $ autocomplete and mention normalization */
  contextVariableOptions: ContextVariableOption[];
};

const EditorTooltipContent = (props: ComponentProps<typeof AppTooltipContent>) => (
  <AppTooltipContent showArrow {...props} />
);

/**
 * BlockNote's built-in DropdownMenuContent renders inline (no Radix Portal),
 * which causes table-handle menus to be clipped by ancestor overflow containers.
 * Using our app's DropdownMenuContent which includes a Portal and proper styling.
 */
const shadCNComponentOverrides = {
  DropdownMenu: {
    ...ShadCNDefaultComponents.DropdownMenu,
    DropdownMenuContent: AppDropdownMenuContent,
    DropdownMenuItem: AppDropdownMenuItem,
  },
  Tooltip: {
    Tooltip: AppTooltip,
    TooltipContent: EditorTooltipContent,
    TooltipProvider: AppTooltipProvider,
    TooltipTrigger: AppTooltipTrigger,
  },
};

const DeleteOnlyDragHandleMenu = (menuProps: DragHandleMenuProps) => {
  const dict = useDictionary();

  return (
    <DragHandleMenu {...menuProps}>
      <RemoveBlockItem {...menuProps}>{dict.drag_handle.delete_menuitem}</RemoveBlockItem>
    </DragHandleMenu>
  );
};

const DeleteOnlySideMenu = (props: SideMenuProps) => (
  <SideMenu {...props} dragHandleMenu={DeleteOnlyDragHandleMenu} />
);

// Run a BlockNote mutation without polluting the undo stack. BlockNote's
// `replaceBlocks` (and other mutators) go through `editor.transact()`, which
// reuses the outer transaction when nested. Setting `addToHistory: false` on
// the outer tr propagates to the inner replaceBlocks dispatch, so ProseMirror
// history skips it. Used for system init / re-normalization — never user edits.
const runWithoutHistory = <
  T extends { transact: (cb: (tr: { setMeta: (k: string, v: unknown) => void }) => void) => void },
>(
  editor: T,
  fn: () => void
) => {
  editor.transact((tr) => {
    tr.setMeta('addToHistory', false);
    fn();
  });
};

export const BlockNoteEditorComponent = ({
  initialMarkdown,
  dictionary = getGuidelinesEditorDefaults().dictionary,
  onEditorReady,
  editable = true,
  onChange,
  enabledTools = [],
  dialogs = [],
  onNavigateToLocalTools,
  contextVariableOptions,
}: Props) => {
  // Creates a new editor instance (BlockNote) configured with our schema
  // and a paste handler that keeps only plain text.
  const editor = useCreateBlockNote({
    schema,
    pasteHandler: createMediaStrippingPasteHandler(),
    dictionary,
  });

  // Ref used to ensure initialization logic runs only once
  const hasInitializedRef = useRef(false);
  const onEditorReadyFiredRef = useRef(false);
  // Flipped after the async content init completes so onEditorReady fires
  // with the editor's document fully loaded (avoids baseline capture from the
  // empty default doc — root cause of the false "unsaved changes" bug).
  const [isContentReady, setIsContentReady] = useState(false);

  // Stores the @ suggestion menu's DOMRect when trigger_workflow is selected,
  // so the ^ menu can be anchored at the same position.
  const workflowAnchorRef = useRef<DOMRect | null>(null);

  // Precompute block type options so the toolbar renders stable entries.
  const blockTypeItems = useMemo(
    () => filterBlockTypeSelectItems(editor.dictionary),
    [editor.dictionary]
  );

  // Lazily build slash menu items based on the current editor instance.
  const getSlashMenuItems = useCallback(() => getCustomSlashMenuItems(editor), [editor]);

  // Build a lookup map keyed by context variable id for quick normalization.
  const contextVariableLookup = useMemo(
    () => buildContextVariableLookup(contextVariableOptions),
    [contextVariableOptions]
  );

  // Derive mention suggestions from the context variable options.
  const mentionMenuItems = useMemo(
    () => getContextVariableMenuItems(editor, contextVariableOptions),
    [contextVariableOptions, editor]
  );

  const handleEmptyStateClick = useCallback(() => {
    onNavigateToLocalTools?.();
  }, [onNavigateToLocalTools]);

  const handleBeforeWorkflowOpen = useCallback(() => {
    // The @ suggestion menu is still rendered when onItemClick fires.
    // Capture its rect so the ^ menu appears at the exact same position.
    const menuEl = document.querySelector('.bn-suggestion-menu');
    if (menuEl) {
      workflowAnchorRef.current = menuEl.getBoundingClientRect();
    }
  }, []);

  // Build local tool menu items based on enabled tools
  const localToolMenuItems = useMemo(
    () =>
      getLocalToolMenuItems(editor, enabledTools, {
        onEmptyStateClick: handleEmptyStateClick,
        onBeforeWorkflowOpen: handleBeforeWorkflowOpen,
      }),
    [editor, enabledTools, handleEmptyStateClick, handleBeforeWorkflowOpen]
  );

  // Build workflow menu items for the ^ suggestion menu (opened programmatically).
  // Only populate when trigger_workflow is enabled so users cannot trigger workflows
  // they haven't opted into via the Local Tools settings.
  const workflowMenuItems = useMemo(
    () => (enabledTools.includes('trigger_workflow') ? getWorkflowMenuItems(editor, dialogs) : []),
    [editor, dialogs, enabledTools]
  );

  // Custom floating-ui options that anchor the ^ menu at the @ menu's caret position
  const workflowFloatingOptions = useMemo(() => {
    const anchorOverride: Middleware = {
      name: 'anchorToAtMenu',
      fn: (state) => {
        const anchor = workflowAnchorRef.current;
        if (!anchor) {
          return {};
        }
        // Convert viewport coords (getBoundingClientRect) to floating-ui's
        // coordinate space by computing the transform from the reference element.
        const refViewport = state.elements.reference.getBoundingClientRect();
        const dx = state.rects.reference.x - refViewport.left;
        const dy = state.rects.reference.y - refViewport.top;
        return { x: anchor.left + dx, y: anchor.top + dy };
      },
    };

    return {
      middleware: [
        anchorOverride,
        flip({ mainAxis: true, crossAxis: false }),
        shift(),
        size({
          apply({ availableHeight, elements }) {
            Object.assign(elements.floating.style, { maxHeight: `${availableHeight - 10}px` });
          },
        }),
      ],
      onOpenChange(open: boolean) {
        if (!open) {
          workflowAnchorRef.current = null;
          editor.suggestionMenus.closeMenu();
        }
      },
    };
  }, [editor.suggestionMenus]);

  // Initialize editor state from the initial markdown only once.
  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }

    hasInitializedRef.current = true;

    (async () => {
      // Parse initial markdown into blocks
      const blocks = await parseMarkdownToBlocks(editor, initialMarkdown);

      // Normalize context-variable placeholders in the parsed blocks
      const { normalized: cvNormalized, changed: cvChanged } = normalizeBlocksWithContextVariables(
        blocks,
        contextVariableLookup
      );

      // Normalize @tool(arg) references into localTool inline nodes
      const cvBlocks = cvChanged ? cvNormalized : blocks;
      const { normalized: ltNormalized, changed: ltChanged } =
        normalizeBlocksWithLocalTools(cvBlocks);

      // Resolve trigger_workflow node_id → dialog name for display labels
      const ltBlocks = ltChanged ? ltNormalized : cvBlocks;
      const { resolved: labelResolved, changed: labelChanged } = resolveLocalToolLabels(
        ltBlocks,
        dialogs
      );

      // Replace the document with the normalized blocks only if normalization
      // produced any change, otherwise use the parsed blocks as-is. Bypass the
      // undo stack so Cmd+Z can't roll back to the editor's empty default doc.
      const blocksToApply = labelChanged ? labelResolved : ltBlocks;
      runWithoutHistory(editor, () => {
        editor.replaceBlocks(editor.document, blocksToApply);
      });
      setIsContentReady(true);
    })().catch((error) => {
      // Don't flip `isContentReady` on failure: firing onEditorReady with the
      // empty default doc would let a subsequent save wipe the real content.
      console.error('Failed to initialize editor content:', error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dialogs handled by separate useEffect below
  }, [editor, initialMarkdown, contextVariableLookup]);

  // Re-resolve trigger_workflow labels when dialogs load/change after init.
  useEffect(() => {
    if (!hasInitializedRef.current || dialogs.length === 0) {
      return;
    }

    const { resolved, changed } = resolveLocalToolLabels(editor.document, dialogs);

    if (!changed) {
      return;
    }

    runWithoutHistory(editor, () => {
      editor.replaceBlocks(editor.document, resolved);
    });
  }, [dialogs, editor]);

  // Re-sync context variable placeholders whenever the lookup changes.
  useEffect(() => {
    if (!hasInitializedRef.current || contextVariableLookup.size === 0) {
      return;
    }

    // When context variables change (add/remove), re-normalize the current
    // document so placeholders stay in sync with the lookup.
    const currentBlocks = editor.document;
    const { normalized, changed } = normalizeBlocksWithContextVariables(
      currentBlocks,
      contextVariableLookup
    );

    if (!changed) {
      return;
    }

    runWithoutHistory(editor, () => {
      editor.replaceBlocks(editor.document, normalized);
    });
  }, [contextVariableLookup, editor]);

  // Track content changes for unsaved changes detection.
  // Gate on `isContentReady` so the block replacements during async init
  // (and subsequent normalization effects) don't propagate as user changes.
  useEditorChange(
    useCallback(() => {
      if (!isContentReady || !onChange) {
        return;
      }
      onChange();
    }, [isContentReady, onChange]),
    editor
  );

  // Expose editor methods to parent component.
  // Depends on `isContentReady` (not the sync `hasInitializedRef`) so the
  // callback fires only after the async content init is done. This guarantees
  // that `getMarkdown()` returns the real document, not the empty default.
  useEffect(() => {
    if (!isContentReady || !onEditorReady || onEditorReadyFiredRef.current) {
      return;
    }
    onEditorReadyFiredRef.current = true;

    const appendContent = async (markdown: string) => {
      const blocksToAppend = await parseMarkdownToBlocks(editor, markdown);
      const lastBlock = editor.document[editor.document.length - 1];

      if (lastBlock) {
        editor.insertBlocks(blocksToAppend, lastBlock, 'after');
        // Move cursor to end of new content
        const newLastBlock = editor.document[editor.document.length - 1];
        editor.setTextCursorPosition(newLastBlock, 'end');
      }
    };

    const getMarkdown = async () => {
      // Custom inline nodes (localTool, mention, contextToken) serialize to empty
      // styled spans, which Turndown collapses into `****` and similar artifacts.
      // Replace them with their text representations before markdown conversion.
      const withoutTools = denormalizeLocalToolNodes(editor.document);
      const blocks = denormalizeContextVariableNodes(withoutTools);

      return editor.blocksToMarkdownLossy(blocks);
    };

    onEditorReady({ appendContent, getMarkdown });
  }, [editor, onEditorReady, isContentReady]);

  return (
    <BlockNoteView
      theme="light" // Force light theme to control the editor via Tailwind classes
      editor={editor}
      formattingToolbar={false}
      sideMenu={false}
      slashMenu={false}
      data-theming-css-blocknote
      editable={editable}
      shadCNComponents={shadCNComponentOverrides as never}
    >
      <SuggestionMenuController
        triggerCharacter="/"
        getItems={async (query) => filterSuggestionItems(getSlashMenuItems(), query)}
      />
      <SuggestionMenuController
        triggerCharacter="$"
        getItems={async (query) => filterSuggestionItems(mentionMenuItems, query)}
      />
      {enabledTools.length > 0 && (
        <SuggestionMenuController
          triggerCharacter="@"
          getItems={async (query) => filterSuggestionItems(localToolMenuItems, query)}
        />
      )}
      {enabledTools.length > 0 && enabledTools.includes('trigger_workflow') && (
        <SuggestionMenuController
          triggerCharacter="^"
          getItems={async (query) => filterSuggestionItems(workflowMenuItems, query)}
          floatingOptions={workflowFloatingOptions}
        />
      )}
      <FormattingToolbarController
        formattingToolbar={() => (
          <FormattingToolbar>
            <BasicTextStyleButton basicTextStyle={'bold'} key={'boldStyleButton'} />
            <BasicTextStyleButton basicTextStyle={'italic'} key={'italicStyleButton'} />
            <BasicTextStyleButton basicTextStyle={'underline'} key={'underlineStyleButton'} />
            <CreateLinkButton key={'createLinkButton'} />

            <BlockTypeSelect key={'blockTypeSelect'} items={blockTypeItems} />
          </FormattingToolbar>
        )}
      />
      <SideMenuController sideMenu={DeleteOnlySideMenu} />
    </BlockNoteView>
  );
};
